import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Eye as EyeIcon, EyeOff, Stethoscope, GraduationCap, ArrowRight, ArrowLeft, Upload, CheckCircle, Shield, BookOpen, Users, FileText, CreditCard, MapPin, Calendar, AlertCircle, Loader2, Headphones } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/App';
import { refreshFileSession } from '@/lib/fileSession';
import { DemoQuickLogin } from '@/components/DemoQuickLogin';
import SiteHeader from '@/components/SiteHeader';

const AuthPage = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('choice'); // choice | login | register
  const [step, setStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [charteAccepted, setCharteAccepted] = useState(false);
  const [form, setForm] = useState({
    email: '', password: '', first_name: '', last_name: '',
    phone: '', region: '', rpps: '',
    studentCardFile: null, studentCardExpiry: '', studentYear: '',
    selectedPlan: '',
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const regions = [
    'Ile-de-France', 'Auvergne-Rhone-Alpes', 'Nouvelle-Aquitaine', 'Occitanie',
    'Hauts-de-France', 'Grand Est', 'Provence-Alpes-Cote d\'Azur', 'Pays de la Loire',
    'Bretagne', 'Normandie', 'Bourgogne-Franche-Comte', 'Centre-Val de Loire',
    'Corse', 'DOM-TOM', 'Autre'
  ];

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: form.email, password: form.password,
      });
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      await refreshFileSession();
      toast.success('Connexion réussie !');
      if (['super_admin', 'regional'].includes(response.data.user.role)) {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Identifiants incorrects';
      toast.error(detail);
    } finally { setLoading(false); }
  };

  const quickLogin = async (account) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/demo-login`, { account });
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      await refreshFileSession();
      toast.success('Connexion réussie !');
      if (['super_admin', 'regional'].includes(response.data.user.role)) {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Identifiants incorrects');
    } finally { setLoading(false); }
  };

  const handleRegister = async () => {
    setLoading(true);
    try {
      const registerData = {
        email: form.email, password: form.password,
        first_name: form.first_name, last_name: form.last_name,
        role: selectedRole, phone: form.phone, region: form.region,
      };
      if (selectedRole === 'ophthalmologist' && form.rpps) {
        registerData.rpps = form.rpps;
      }
      if (selectedRole === 'intern' && form.studentCardExpiry) {
        registerData.student_card_expiry = form.studentCardExpiry;
      }
      if (selectedRole === 'intern' && form.studentYear) {
        registerData.student_year = form.studentYear;
      }

      const response = await axios.post(`${API}/auth/register`, registerData);
      const token = response.data.token;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      await refreshFileSession();

      // Upload student card for interns
      if (selectedRole === 'intern' && form.studentCardFile) {
        const fd = new FormData();
        fd.append('file', form.studentCardFile);
        if (form.studentCardExpiry) {
          fd.append('expiry_date', form.studentCardExpiry);
        }
        await axios.post(`${API}/upload/student-card`, fd, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        });
      }

      if (selectedRole === 'intern') {
        toast.success('Inscription réussie ! Votre compte sera activé après validation de votre carte étudiante.');
        setMode('intern-success');
      } else {
        toast.success('Inscription réussie !');
        navigate('/adhesion');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally { setLoading(false); }
  };

  const inputClass = 'w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:border-[#35AFE4] focus:ring-2 focus:ring-[#35AFE4]/10 outline-none transition-all bg-gray-50/50 hover:bg-white';
  const labelClass = 'block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5';

  // ===== REGISTER STEPS =====
  const registerSteps = selectedRole === 'intern'
    ? ['Identité', 'Coordonnées', 'Carte étudiante', 'Charte', 'Confirmation']
    : ['Identité', 'Coordonnées', 'Adhésion', 'Charte', 'Confirmation'];
  const totalSteps = registerSteps.length;
  const progress = ((step + 1) / totalSteps) * 100;

  const canAdvance = () => {
    if (step === 0) return form.first_name && form.last_name && form.email && form.password;
    if (step === 1) return true;
    if (step === 2 && selectedRole === 'intern') return form.studentCardFile && form.studentCardExpiry && form.studentYear;
    if (step === 2 && selectedRole === 'ophthalmologist') return form.selectedPlan;
    if (step === 3) return charteAccepted;
    return true;
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f8f9fb]" style={{ fontFamily: "'Outfit', 'Roboto', sans-serif" }} data-testid="auth-page">
      <SiteHeader />

      <div className="flex-1 flex items-center justify-center px-4 py-8 sm:py-12">

        {/* ===== CHOICE SCREEN ===== */}
        {mode === 'choice' && (
          <div className="w-full max-w-lg" data-testid="auth-choice">
            <div className="text-center mb-8">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight mb-2">Bienvenue sur SFOALC</h1>
              <p className="text-sm text-gray-500">Accédez à votre espace membre</p>
            </div>

            {/* Login card */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-4">
              <button onClick={() => setMode('login')} className="w-full p-6 flex items-center gap-4 hover:bg-gray-50/50 transition-all group" data-testid="btn-go-login">
                <div className="w-12 h-12 rounded-xl bg-[#1e3a5f] flex items-center justify-center flex-shrink-0">
                  <Shield size={20} className="text-white" />
                </div>
                <div className="text-left flex-1">
                  <p className="text-base font-bold text-gray-900">J'ai déjà un compte</p>
                  <p className="text-sm text-gray-400">Connectez-vous à votre espace</p>
                </div>
                <ArrowRight size={18} className="text-gray-300 group-hover:text-[#35AFE4] transition-colors" />
              </button>
            </div>

            {/* Register options */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-6 pt-5 pb-3">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Créer un compte</p>
              </div>

              <button onClick={() => { setSelectedRole('ophthalmologist'); setMode('register'); setStep(0); }}
                className="w-full px-6 py-4 flex items-center gap-4 hover:bg-gray-50/50 transition-all group border-t border-gray-50" data-testid="btn-register-ophtalmo">
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <Stethoscope size={20} className="text-[#1e3a5f]" />
                </div>
                <div className="text-left flex-1">
                  <p className="text-base font-bold text-gray-900">Ophtalmologiste</p>
                  <p className="text-xs text-gray-400">Adhésion payante - Accès complet + fiche annuaire</p>
                </div>
                <ArrowRight size={18} className="text-gray-300 group-hover:text-[#35AFE4] transition-colors" />
              </button>

              <button onClick={() => { setSelectedRole('intern'); setMode('register'); setStep(0); }}
                className="w-full px-6 py-4 flex items-center gap-4 hover:bg-gray-50/50 transition-all group border-t border-gray-50" data-testid="btn-register-intern">
                <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                  <GraduationCap size={20} className="text-emerald-600" />
                </div>
                <div className="text-left flex-1">
                  <p className="text-base font-bold text-gray-900">Interne</p>
                  <p className="text-xs text-gray-400">Gratuit - Carte étudiante requise, validation admin</p>
                </div>
                <ArrowRight size={18} className="text-gray-300 group-hover:text-emerald-500 transition-colors" />
              </button>
            </div>

            {/* Spacer */}
            <div className="mt-4" />
          </div>
        )}

        {/* ===== LOGIN ===== */}
        {mode === 'login' && (
          <div className="w-full max-w-md" data-testid="login-form">
            <button onClick={() => setMode('choice')} className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-600 mb-6 transition-colors" data-testid="btn-back-choice">
              <ArrowLeft size={16} /> Retour
            </button>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8">
              <div className="text-center mb-6">
                <div className="w-14 h-14 rounded-xl bg-[#1e3a5f] flex items-center justify-center mx-auto mb-4">
                  <Shield size={24} className="text-white" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 tracking-tight">Connexion</h2>
                <p className="text-sm text-gray-400 mt-1">Accédez à votre espace SFOALC</p>
              </div>

              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className={labelClass}>Email</label>
                  <input type="email" value={form.email} onChange={e => set('email', e.target.value)}
                    className={inputClass} required data-testid="field-email" placeholder="votre@email.com" />
                </div>
                <div>
                  <label className={labelClass}>Mot de passe</label>
                  <div className="relative">
                    <input type={showPassword ? 'text' : 'password'} value={form.password} onChange={e => set('password', e.target.value)}
                      className={inputClass + ' pr-10'} required data-testid="field-password" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showPassword ? <EyeOff size={16} /> : <EyeIcon size={16} />}
                    </button>
                  </div>
                </div>
                <button type="submit" disabled={loading} data-testid="submit-btn"
                  className="w-full py-3 rounded-xl bg-[#1e3a5f] hover:bg-[#162d4a] text-white font-bold text-sm transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                  {loading ? <Loader2 className="animate-spin" size={16} /> : null}
                  {loading ? 'Connexion...' : 'Se connecter'}
                </button>
              </form>

              <p className="text-center text-sm text-gray-400 mt-5">
                Pas encore de compte ?{' '}
                <button onClick={() => setMode('choice')} className="text-[#35AFE4] font-semibold hover:underline">Créer un compte</button>
              </p>

              <DemoQuickLogin onLogin={quickLogin} loading={loading} />
            </div>
          </div>
        )}

        {/* ===== REGISTER ONBOARDING ===== */}
        {mode === 'register' && (
          <div className="w-full max-w-lg" data-testid="register-form">
            <button onClick={() => { if (step === 0) { setMode('choice'); } else { setStep(step - 1); } }}
              className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-600 mb-5 transition-colors" data-testid="btn-back-register">
              <ArrowLeft size={16} /> {step === 0 ? 'Retour' : 'Étape précédente'}
            </button>

            {/* Role badge */}
            <div className="flex items-center gap-2 mb-4">
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${selectedRole === 'ophthalmologist' ? 'bg-blue-50' : 'bg-emerald-50'}`}>
                {selectedRole === 'ophthalmologist' ? <Stethoscope size={14} className="text-[#1e3a5f]" /> : <GraduationCap size={14} className="text-emerald-600" />}
              </div>
              <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                Inscription {selectedRole === 'ophthalmologist' ? 'Ophtalmologiste' : 'Interne'}
              </span>
            </div>

            {/* Progress */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                {registerSteps.map((s, i) => (
                  <span key={s} className={`text-[11px] font-semibold ${i <= step ? 'text-gray-800' : 'text-gray-300'}`}>{s}</span>
                ))}
              </div>
              <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${progress}%`, background: selectedRole === 'ophthalmologist' ? '#1e3a5f' : '#10b981' }} />
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8">

              {/* Step 0: Identity */}
              {step === 0 && (
                <div className="space-y-4 animate-fadeIn" data-testid="step-identity">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Votre identité</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelClass}>Prénom *</label>
                      <input type="text" value={form.first_name} onChange={e => set('first_name', e.target.value)}
                        className={inputClass} required data-testid="field-firstname" />
                    </div>
                    <div>
                      <label className={labelClass}>Nom *</label>
                      <input type="text" value={form.last_name} onChange={e => set('last_name', e.target.value)}
                        className={inputClass} required data-testid="field-lastname" />
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Email *</label>
                    <input type="email" value={form.email} onChange={e => set('email', e.target.value)}
                      className={inputClass} required data-testid="field-email" placeholder="votre@email.com" />
                  </div>
                  <div>
                    <label className={labelClass}>Mot de passe *</label>
                    <div className="relative">
                      <input type={showPassword ? 'text' : 'password'} value={form.password} onChange={e => set('password', e.target.value)}
                        className={inputClass + ' pr-10'} required data-testid="field-password" placeholder="Minimum 6 caractères" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        {showPassword ? <EyeOff size={16} /> : <EyeIcon size={16} />}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 1: Coordinates */}
              {step === 1 && (
                <div className="space-y-4 animate-fadeIn" data-testid="step-coords">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Vos coordonnées</h3>
                  <div>
                    <label className={labelClass}>Téléphone</label>
                    <input type="tel" value={form.phone} onChange={e => set('phone', e.target.value)}
                      className={inputClass} data-testid="field-phone" placeholder="06 12 34 56 78" />
                  </div>
                  <div>
                    <label className={labelClass}>Région</label>
                    <select value={form.region} onChange={e => set('region', e.target.value)}
                      className={inputClass} data-testid="field-region">
                      <option value="">Sélectionnez votre région</option>
                      {regions.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  {selectedRole === 'ophthalmologist' && (
                    <div>
                      <label className={labelClass}>N° RPPS</label>
                      <input type="text" value={form.rpps} onChange={e => set('rpps', e.target.value)}
                        className={inputClass} data-testid="field-rpps" placeholder="Votre numéro RPPS" />
                    </div>
                  )}
                </div>
              )}

              {/* Step 2 (Intern only): Student card */}
              {step === 2 && selectedRole === 'intern' && (
                <div className="space-y-5 animate-fadeIn" data-testid="step-student-card">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Carte étudiante & Formation</h3>
                  <p className="text-sm text-gray-400 mb-4">Votre carte sera vérifiée par l'administration avant l'activation de votre compte.</p>

                  {/* Info box */}
                  <div className="flex items-start gap-3 bg-amber-50 rounded-xl p-4 border border-amber-100">
                    <AlertCircle size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-amber-700 leading-relaxed">
                      <p className="font-semibold mb-1">Important</p>
                      <p>Votre accès sera validé jusqu'à la date d'expiration de votre carte. Passé cette date, votre compte sera suspendu et vous devrez fournir une nouvelle carte chaque année.</p>
                    </div>
                  </div>

                  {/* Année de formation */}
                  <div>
                    <label className={labelClass}>Année de formation *</label>
                    <select value={form.studentYear} onChange={e => set('studentYear', e.target.value)}
                      className={inputClass} required data-testid="field-student-year">
                      <option value="">Sélectionnez votre année</option>
                      <optgroup label="DES Ophtalmologie">
                        <option value="DES1">DES 1 — 1ère année</option>
                        <option value="DES2">DES 2 — 2ème année</option>
                        <option value="DES3">DES 3 — 3ème année</option>
                        <option value="DES4">DES 4 — 4ème année</option>
                        <option value="DES5">DES 5 — 5ème année</option>
                      </optgroup>
                      <optgroup label="Externe">
                        <option value="DFASM2">DFASM 2 — Externe</option>
                        <option value="DFASM3">DFASM 3 — Externe</option>
                      </optgroup>
                      <optgroup label="Post-internat">
                        <option value="CCA">CCA / Assistant</option>
                        <option value="autre">Autre</option>
                      </optgroup>
                    </select>
                  </div>

                  {/* File upload */}
                  <div>
                    <label className={labelClass}>Télécharger votre carte *</label>
                    <div className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-all ${
                      form.studentCardFile ? 'border-emerald-300 bg-emerald-50/30' : 'border-gray-200 hover:border-[#35AFE4] bg-gray-50/50'
                    }`}>
                      {form.studentCardFile ? (
                        <div className="flex items-center justify-center gap-3">
                          <CheckCircle size={20} className="text-emerald-500" />
                          <div>
                            <p className="text-sm font-semibold text-gray-800">{form.studentCardFile.name}</p>
                            <p className="text-xs text-gray-400">{(form.studentCardFile.size / 1024).toFixed(0)} Ko</p>
                          </div>
                          <button onClick={() => set('studentCardFile', null)} className="text-xs text-red-400 hover:text-red-600 ml-2 font-semibold">Supprimer</button>
                        </div>
                      ) : (
                        <>
                          <Upload size={24} className="mx-auto text-gray-300 mb-2" />
                          <p className="text-sm text-gray-500 font-medium">Glissez votre fichier ou cliquez</p>
                          <p className="text-xs text-gray-400 mt-1">JPEG, PNG ou PDF (max 5 Mo)</p>
                        </>
                      )}
                      <input type="file" accept="image/*,.pdf" onChange={e => set('studentCardFile', e.target.files[0])}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-testid="field-student-card" />
                    </div>
                  </div>

                  {/* Expiry date */}
                  <div>
                    <label className={labelClass}>Date d'expiration de la carte *</label>
                    <input type="date" value={form.studentCardExpiry} onChange={e => set('studentCardExpiry', e.target.value)}
                      className={inputClass} required data-testid="field-card-expiry" />
                  </div>
                </div>
              )}

              {/* Step 2 (Ophthalmologist): Plan selection */}
              {step === 2 && selectedRole === 'ophthalmologist' && (
                <div className="space-y-5 animate-fadeIn" data-testid="step-plan-selection">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Choisissez votre formule d'adhésion</h3>
                  <p className="text-sm text-gray-400 mb-4">Deux formules disponibles pour les ophtalmologistes praticiens</p>

                  <div className="space-y-3">
                    {/* Soutien plan - highlighted */}
                    <button
                      onClick={() => set('selectedPlan', 'soutien')}
                      className={`relative w-full text-left p-5 rounded-xl border-2 transition-all ${form.selectedPlan === 'soutien' ? 'border-[#35AFE4] bg-[#dceef5] shadow-lg ring-2 ring-[#35AFE4]/20' : 'border-[#b8dce8] bg-[#eef6fa] hover:border-[#35AFE4]/60'}`}
                      data-testid="plan-soutien"
                    >
                      <span className="absolute -top-2.5 left-5 bg-[#35AFE4] text-white text-[10px] font-bold px-3 py-0.5 rounded-full uppercase tracking-wider">Populaire</span>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${form.selectedPlan === 'soutien' ? 'border-[#35AFE4]' : 'border-[#35AFE4]/40'}`}>
                            {form.selectedPlan === 'soutien' && <div className="w-2.5 h-2.5 rounded-full bg-[#35AFE4]" />}
                          </div>
                          <span className="font-bold text-[#1e3a5f] text-base">Adhésion Soutien</span>
                        </div>
                        <span className="text-xl font-bold text-[#35AFE4]">150€<span className="text-sm font-normal text-gray-400">/an</span></span>
                      </div>
                      <p className="text-xs text-[#3a6b84] ml-8">Adhésion complète avec soutien renforcé à l'association. Accès complet aux formations, replays, publications, forum privé et référencement annuaire.</p>
                    </button>

                    {/* Standard plan */}
                    <button
                      onClick={() => set('selectedPlan', 'standard')}
                      className={`w-full text-left p-5 rounded-xl border-2 transition-all ${form.selectedPlan === 'standard' ? 'border-[#35AFE4] bg-[#eaf6fc] shadow-md' : 'border-gray-200 hover:border-gray-300'}`}
                      data-testid="plan-standard"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${form.selectedPlan === 'standard' ? 'border-[#35AFE4]' : 'border-gray-300'}`}>
                            {form.selectedPlan === 'standard' && <div className="w-2.5 h-2.5 rounded-full bg-[#35AFE4]" />}
                          </div>
                          <span className="font-bold text-gray-900 text-base">Adhésion Standard</span>
                        </div>
                        <span className="text-xl font-bold text-[#35AFE4]">100€<span className="text-sm font-normal text-gray-400">/an</span></span>
                      </div>
                      <p className="text-xs text-gray-500 ml-8">Adhésion standard. Accès aux formations, replays, publications, forum privé et référencement annuaire.</p>
                    </button>
                  </div>

                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-100 mt-4">
                    <p className="text-xs text-blue-700">Le paiement sera effectué via HelloAsso après la création de votre compte.</p>
                  </div>
                </div>
              )}

              {/* Step 3: Charte de bonne pratique */}
              {step === 3 && (
                <div className="space-y-5 animate-fadeIn" data-testid="step-charte">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Charte de bonne pratique</h3>
                  <p className="text-sm text-gray-400 mb-4">Veuillez lire et accepter la charte avant de finaliser votre inscription</p>

                  <div className="bg-gray-50 rounded-xl border border-gray-200 p-5 max-h-64 overflow-y-auto text-[13px] text-gray-600 leading-relaxed space-y-3" data-testid="charte-content">
                    <p className="font-bold text-[#1e3a5f]">CHARTE DE BONNE PRATIQUE - SFOALC</p>
                    <p>En tant que membre de la Société Française des Ophtalmologistes Adaptateurs de Lentilles de Contact, je m'engage à :</p>
                    <ul className="list-disc pl-5 space-y-2">
                      <li><strong>Exercer avec compétence et intégrité</strong> : adapter les lentilles de contact conformément aux bonnes pratiques médicales et aux recommandations en vigueur.</li>
                      <li><strong>Respecter le secret médical</strong> : garantir la confidentialité des informations échangées sur la plateforme, notamment sur le forum privé.</li>
                      <li><strong>Contribuer positivement</strong> : partager mes connaissances et expériences avec bienveillance sur le forum et lors des événements SFOALC.</li>
                      <li><strong>Respecter les autres membres</strong> : maintenir un dialogue courtois et constructif dans toutes les interactions au sein de la communauté.</li>
                      <li><strong>Ne pas utiliser la plateforme à des fins commerciales</strong> : les échanges sur le forum et l'annuaire ont vocation purement médicale et informative.</li>
                      <li><strong>Maintenir mes informations à jour</strong> : mettre à jour régulièrement mes coordonnées et ma fiche annuaire pour garantir l'exactitude de l'annuaire public.</li>
                      <li><strong>Signaler tout contenu inapproprié</strong> : alerter l'administration de tout contenu qui ne respecterait pas la présente charte ou la déontologie médicale.</li>
                      <li><strong>Respecter la propriété intellectuelle</strong> : ne pas reproduire ou diffuser les contenus de formation sans autorisation préalable de la SFOALC.</li>
                    </ul>
                    <p className="text-xs text-gray-400 mt-3 pt-3 border-t border-gray-200">Le non-respect de cette charte peut entraîner la suspension ou la suppression du compte membre, après examen par le bureau de la SFOALC.</p>
                  </div>

                  <label className="flex items-start gap-3 p-4 bg-white rounded-xl border-2 cursor-pointer transition-all hover:border-[#35AFE4]/30"
                    style={{ borderColor: charteAccepted ? '#35AFE4' : '#e5e7eb' }}>
                    <input type="checkbox" checked={charteAccepted} onChange={(e) => setCharteAccepted(e.target.checked)}
                      className="mt-0.5 w-5 h-5 accent-[#35AFE4] rounded" data-testid="charte-checkbox" />
                    <div>
                      <p className="text-sm font-semibold text-gray-800">J'ai lu et j'accepte la charte de bonne pratique</p>
                      <p className="text-xs text-gray-400 mt-0.5">Cette acceptation est obligatoire pour créer votre compte</p>
                    </div>
                  </label>
                </div>
              )}

              {/* Last step: Confirmation */}
              {step === totalSteps - 1 && (
                <div className="space-y-4 animate-fadeIn" data-testid="step-confirmation">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Récapitulatif</h3>

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between py-2 border-b border-gray-50">
                      <span className="text-gray-400">Nom</span>
                      <span className="font-semibold text-gray-800">{form.first_name} {form.last_name}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-50">
                      <span className="text-gray-400">Email</span>
                      <span className="font-semibold text-gray-800">{form.email}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-50">
                      <span className="text-gray-400">Profil</span>
                      <span className="font-semibold text-gray-800">{selectedRole === 'ophthalmologist' ? 'Ophtalmologiste' : 'Interne'}</span>
                    </div>
                    {form.region && (
                      <div className="flex justify-between py-2 border-b border-gray-50">
                        <span className="text-gray-400">Région</span>
                        <span className="font-semibold text-gray-800">{form.region}</span>
                      </div>
                    )}
                    {selectedRole === 'ophthalmologist' && form.selectedPlan && (
                      <div className="flex justify-between py-2 border-b border-gray-50">
                        <span className="text-gray-400">Formule</span>
                        <span className="font-semibold text-[#35AFE4]">{form.selectedPlan === 'soutien' ? 'Soutien — 150€/an' : 'Standard — 100€/an'}</span>
                      </div>
                    )}
                    {selectedRole === 'intern' && form.studentYear && (
                      <div className="flex justify-between py-2 border-b border-gray-50">
                        <span className="text-gray-400">Année de formation</span>
                        <span className="font-semibold text-gray-800">{form.studentYear}</span>
                      </div>
                    )}
                    {selectedRole === 'intern' && form.studentCardExpiry && (
                      <div className="flex justify-between py-2 border-b border-gray-50">
                        <span className="text-gray-400">Carte valide jusqu'au</span>
                        <span className="font-semibold text-gray-800">{new Date(form.studentCardExpiry).toLocaleDateString('fr-FR')}</span>
                      </div>
                    )}
                  </div>

                  {/* Role info */}
                  {selectedRole === 'ophthalmologist' ? (
                    <div className="bg-blue-50 rounded-xl p-4 border border-blue-100 space-y-2">
                      <p className="text-xs font-bold text-blue-800 uppercase tracking-wider">Après inscription</p>
                      <p className="text-xs text-blue-700">Vous serez redirigé vers la page d'adhésion pour procéder au paiement de votre formule {form.selectedPlan === 'soutien' ? 'Soutien (150€)' : 'Standard (100€)'}. Votre adhésion vous donnera accès aux contenus privés et à la création de fiches dans l'annuaire.</p>
                    </div>
                  ) : (
                    <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100 space-y-2">
                      <p className="text-xs font-bold text-emerald-800 uppercase tracking-wider">Prochaine étape</p>
                      <p className="text-xs text-emerald-700">Votre carte étudiante sera vérifiée par l'administration. Vous serez notifié dès que votre compte sera activé. L'accès est gratuit et validé jusqu'à l'expiration de votre carte.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Navigation */}
              <div className="flex justify-between mt-6 pt-4 border-t border-gray-50">
                {step > 0 && step < totalSteps - 1 && (
                  <button onClick={() => setStep(step - 1)}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500 hover:bg-gray-50 transition-all" data-testid="btn-prev-step">
                    <ArrowLeft size={16} /> Précédent
                  </button>
                )}
                {step < totalSteps - 1 ? (
                  <button onClick={() => setStep(step + 1)} disabled={!canAdvance()}
                    className="ml-auto flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#1e3a5f] hover:bg-[#162d4a] text-white text-sm font-bold transition-all disabled:opacity-40" data-testid="btn-next-step">
                    Suivant <ArrowRight size={16} />
                  </button>
                ) : (
                  <button onClick={handleRegister} disabled={loading}
                    className="ml-auto flex items-center gap-2 px-6 py-3 rounded-xl bg-[#1e3a5f] hover:bg-[#162d4a] text-white text-sm font-bold transition-all disabled:opacity-50" data-testid="submit-btn">
                    {loading ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle size={16} />}
                    {loading ? 'Création...' : 'Créer mon compte'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ===== INTERN SUCCESS ===== */}
        {mode === 'intern-success' && (
          <div className="w-full max-w-md" data-testid="intern-success">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-5">
                <CheckCircle size={32} className="text-emerald-500" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Inscription réussie !</h2>
              <p className="text-sm text-gray-500 mb-6 max-w-sm mx-auto leading-relaxed">
                Votre compte a été créé. Votre carte étudiante va être vérifiée par l'administration de la SFOALC. Vous recevrez une notification dès que votre accès sera activé.
              </p>
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-100 text-left mb-6">
                <div className="flex items-start gap-2">
                  <AlertCircle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-700">
                    Vous ne pourrez pas vous connecter tant que votre carte n'a pas été validée. Si votre carte expire, votre accès sera automatiquement suspendu.
                  </p>
                </div>
              </div>
              <button onClick={() => navigate('/')} className="px-6 py-2.5 rounded-xl bg-[#1e3a5f] text-white text-sm font-bold hover:bg-[#162d4a] transition-all" data-testid="btn-go-home">
                Retour à l'accueil
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .animate-fadeIn { animation: fadeIn 0.35s ease-out; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

export default AuthPage;
